import { useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fireTrackAnalytics, fireUIAnalytics } from '@atlassian/analytics-bridge';
import { assert, AssertionError } from '@atlassian/eoc-common';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import { type Heartbeat } from '../../common/types';
import { useHeartbeatsService } from '../../services/heartbeats';
import { heartbeatsResource } from '../../services/resources';

export const useDeleteModalController = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { isDeleting, deleteHeartbeat } = useHeartbeatsService();

	const { update } = useResource(heartbeatsResource);

	const [deletingItem, setDeletingItem] = useState<Heartbeat>();
	const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

	const handleDeleteClick = useCallback(
		(heartbeat: Heartbeat) => {
			const event = createAnalyticsEvent({});
			fireUIAnalytics(event, 'button clicked', 'deleteButton');

			setDeletingItem(heartbeat);
			setDeleteModalOpen(true);
		},
		[createAnalyticsEvent],
	);

	const handleModalClose = useCallback(() => {
		setDeleteModalOpen(false);
		setDeletingItem(undefined);
	}, []);

	const handleDelete = useCallback(() => {
		assert(
			!!deletingItem,
			new AssertionError('useDeleteModalController', 'deleting item should not be undefined!'),
		);

		return deleteHeartbeat(deletingItem.name)
			.then(() => {
				update((data) => {
					assert(
						!!data,
						new AssertionError(
							'useDeleteModalController',
							"heartbeat's resource data should no be undefined!",
						),
					);

					return {
						...data,
						heartbeats: data.heartbeats.filter(({ name }) => name !== deletingItem.name),
					};
				});

				handleModalClose();
			})
			.then(() => {
				const event = createAnalyticsEvent({});
				fireTrackAnalytics(event, 'heartbeatDelete succeeded');
			})
			.catch(() => {
				const event = createAnalyticsEvent({});
				fireTrackAnalytics(event, 'heartbeatDelete failed');
			});
	}, [createAnalyticsEvent, deleteHeartbeat, deletingItem, handleModalClose, update]);

	return {
		deletingItem,
		isDeleteModalOpen,
		handleDeleteClick,
		handleModalClose,
		isDeleting,
		handleDelete,
	};
};
